/**
 * Notify — tokenised preferences page (stay-informed-channels.md §3.2, Phase 4). Route
 * `/notify/preferences/:token`. Same anti-enumeration + no-login capability-URL model as
 * NotifyLandingPage's confirm/unsubscribe (an unrecognized token reads identically to a genuinely
 * expired one; an already-unsubscribed token reads as "unsubscribed-since" — there is nothing to
 * manage preferences on once fully opted out).
 *
 * GET loads the subscriber's current channel selection + the tenant's active channel list, PLUS the
 * postal-updates.md extension: delivery method (email/mail/both) and mailing address, editable here
 * the same as SubscribePage (DeliveryMethodField + MailingAddressFields). Re-checking and saving
 * PUTs the new selection. Unchecking ALL channels routes to a REAL unsubscribe server-side (§3.2
 * "uncheck-all → unsubscribe") — the UI mirrors that outcome with the same "you're unsubscribed"
 * copy NotifyLandingPage uses, not a silent "saved" toast, so a resident who meant to opt out
 * entirely gets an unambiguous confirmation.
 *
 * Focus moves to the result heading on load/save (WCAG 2.1 AA, matches NotifyLandingPage §2.3/§3.3).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPreferences, updatePreferences, ApiError } from "../../api/index.js";
import type { ChannelView, DeliveryMethod, MailingAddress } from "../../api/types.js";
import { Button } from "../../components/Button.js";
import { CheckIcon, AlertIcon } from "../../components/icons.js";
import { TownHeader } from "../../components/TownHeader.js";
import { ResidentThemeRoot } from "../../components/ResidentThemeRoot.js";
import { useTheme } from "../../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../../components/useDocumentTitle.js";
import { useEmbedClose } from "../../components/useEmbedClose.js";
import { DeliveryMethodField } from "../../components/DeliveryMethodField.js";
import {
  MailingAddressFields,
  emptyMailingAddress,
  validateMailingAddress,
  type MailingAddressErrors,
} from "../../components/MailingAddressFields.js";

type LoadState = "loading" | "success" | "expired" | "unsubscribed-since" | "error";
type SaveState = "idle" | "saving" | "saved" | "unsubscribed" | "error";

interface Props {
  embed?: boolean;
}

const LEGEND_ID = "field-preferences-channels-legend";

export function PreferencesPage({ embed = false }: Props): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { config } = useTheme();
  useDocumentTitle(pageTitle("Manage Your Updates", config.townName));
  useEmbedClose(embed);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [channels, setChannels] = useState<ChannelView[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("email");
  const [mailingAddress, setMailingAddress] = useState<MailingAddress>(emptyMailingAddress());
  const [addressErrors, setAddressErrors] = useState<MailingAddressErrors>({});
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const headingRef = useRef<HTMLHeadingElement>(null);

  const needsAddress = deliveryMethod === "mail" || deliveryMethod === "both";

  useEffect(() => {
    let live = true;
    if (!token) {
      setLoadState("expired");
      return;
    }
    getPreferences(token)
      .then((res) => {
        if (!live) return;
        if (res.state === "expired" || res.state === "unsubscribed-since") {
          setLoadState(res.state);
          return;
        }
        setChannels(res.channels ?? []);
        setSelected(new Set(res.selected ?? []));
        setDeliveryMethod(res.deliveryMethod || "email");
        setMailingAddress(res.mailingAddress ?? emptyMailingAddress());
        setLoadState("success");
      })
      .catch((err) => {
        if (!live) return;
        // Anti-enumeration: a network/validation failure reads as the generic expired copy,
        // matching NotifyLandingPage's confirm/unsubscribe error handling.
        setLoadState(err instanceof ApiError ? "expired" : "error");
      });
    return () => {
      live = false;
    };
  }, [token]);

  useEffect(() => {
    if (loadState !== "loading") headingRef.current?.focus();
  }, [loadState]);

  useEffect(() => {
    if (saveState === "saved" || saveState === "unsubscribed") headingRef.current?.focus();
  }, [saveState]);

  const toggleChannel = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSave = async () => {
    if (!token || saveState === "saving") return;
    if (needsAddress) {
      const errs = validateMailingAddress(mailingAddress);
      setAddressErrors(errs);
      if (Object.keys(errs).length > 0) return;
    } else {
      setAddressErrors({});
    }
    setSaveState("saving");
    try {
      const res = await updatePreferences(
        token,
        [...selected],
        deliveryMethod,
        needsAddress
          ? {
              ...mailingAddress,
              state: mailingAddress.state.trim().toUpperCase(),
              line2: mailingAddress.line2?.trim() || undefined,
            }
          : undefined,
      );
      if (res.state === "unsubscribed") {
        setSaveState("unsubscribed");
      } else {
        setSelected(new Set(res.selected ?? []));
        if (res.deliveryMethod) setDeliveryMethod(res.deliveryMethod);
        if (res.mailingAddress !== undefined)
          setMailingAddress(res.mailingAddress ?? emptyMailingAddress());
        setSaveState("saved");
      }
    } catch {
      setSaveState("error");
    }
  };

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <TownHeader embed={embed} />
      <main className="td-confirm">
        {loadState === "loading" ? (
          <p role="status" className="td-track__loading">
            Loading your preferences…
          </p>
        ) : loadState === "expired" || loadState === "error" ? (
          <>
            <p className="td-sr-only" aria-live="polite">
              That link didn't work.
            </p>
            <div className="td-confirm__mark" aria-hidden="true">
              <AlertIcon width={28} height={28} />
            </div>
            <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
              That link didn't work.
            </h1>
            <p className="td-confirm__sub">
              This preferences link may have expired or is no longer valid. If you're still getting
              emails you don't want, you can unsubscribe from any update email, or subscribe again
              below.
            </p>
            <div className="td-confirm__actions">
              <Link to="/notify/subscribe" className="td-btn td-btn--primary">
                Subscribe again
              </Link>
              {config.websiteUrl ? (
                <a
                  href={config.websiteUrl}
                  rel="noopener noreferrer"
                  className="td-btn td-btn--secondary"
                >
                  Back to {config.townName}'s website
                </a>
              ) : null}
            </div>
          </>
        ) : loadState === "unsubscribed-since" ? (
          <>
            <p className="td-sr-only" aria-live="polite">
              This link is no longer active.
            </p>
            <div className="td-confirm__mark" aria-hidden="true">
              <AlertIcon width={28} height={28} />
            </div>
            <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
              This link is no longer active.
            </h1>
            <p className="td-confirm__sub">
              It looks like you unsubscribed from {config.townName}'s updates. Want to subscribe
              again?
            </p>
            <div className="td-confirm__actions">
              <Link to="/notify/subscribe" className="td-btn td-btn--primary">
                Subscribe again
              </Link>
              {config.websiteUrl ? (
                <a
                  href={config.websiteUrl}
                  rel="noopener noreferrer"
                  className="td-btn td-btn--secondary"
                >
                  Back to {config.townName}'s website
                </a>
              ) : null}
            </div>
          </>
        ) : saveState === "unsubscribed" ? (
          <>
            <p className="td-sr-only" aria-live="polite">
              You're unsubscribed.
            </p>
            <div className="td-confirm__mark" aria-hidden="true">
              <CheckIcon width={28} height={28} />
            </div>
            <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
              You're unsubscribed.
            </h1>
            <p className="td-confirm__sub">
              You won't get any more city update emails from {config.townName}. If that was a
              mistake, you can subscribe again anytime.
            </p>
            <div className="td-confirm__actions">
              <Link to="/notify/subscribe" className="td-btn td-btn--primary">
                Subscribe again
              </Link>
              {config.websiteUrl ? (
                <a
                  href={config.websiteUrl}
                  rel="noopener noreferrer"
                  className="td-btn td-btn--secondary"
                >
                  Back to {config.townName}'s website
                </a>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p className="td-sr-only" aria-live="polite">
              {saveState === "saved" ? "Preferences saved." : "Manage your email preferences."}
            </p>
            <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
              Manage your updates
            </h1>
            <p className="td-confirm__sub">
              Choose which updates you'd like from {config.townName}. Uncheck everything to
              unsubscribe from all city update emails.
            </p>

            <DeliveryMethodField
              idPrefix="preferences"
              value={deliveryMethod}
              onChange={setDeliveryMethod}
            />

            {needsAddress ? (
              <MailingAddressFields
                idPrefix="preferences"
                value={mailingAddress}
                onChange={(next) => {
                  setMailingAddress(next);
                  if (Object.keys(addressErrors).length) setAddressErrors({});
                }}
                errors={addressErrors}
              />
            ) : null}

            {channels.length ? (
              <fieldset className="td-fieldset">
                <legend className="td-fieldset__legend" id={LEGEND_ID}>
                  What updates do you want?
                </legend>
                <div className="td-channel-cards">
                  {channels.map((c) => {
                    const checked = selected.has(c.id);
                    const inputId = `field-preferences-channel-${c.id}`;
                    return (
                      <label
                        key={c.id}
                        htmlFor={inputId}
                        className={`td-channel-card${checked ? " is-selected" : ""}`}
                      >
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleChannel(c.id)}
                        />
                        <span className="td-channel-card__label">{c.label}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ) : (
              <p className="td-confirm__sub">
                {config.townName} doesn't have any specific update topics set up yet — you'll get
                all city updates.
              </p>
            )}

            <div className="td-submit">
              <Button
                onClick={onSave}
                disabled={saveState === "saving"}
                aria-busy={saveState === "saving"}
              >
                {saveState === "saving" ? "Saving…" : "Save preferences"}
              </Button>
            </div>
            <p aria-live="polite" className="td-submit__hint">
              {saveState === "saved" ? "Saved — your preferences were updated." : null}
              {saveState === "error" ? "We couldn't save that — try again." : null}
            </p>
          </>
        )}
      </main>
      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        <Link to="/notify/manage">Lost this link?</Link>
      </footer>
    </ResidentThemeRoot>
  );
}
